import { useState, type ReactElement } from "react";
import {
	createRootRoute,
	Link,
	Outlet,
	useRouterState,
} from "@tanstack/react-router";
import { signOut, useSession } from "@baseline/web/auth";
import { FiSearch, FiMessageSquare } from "react-icons/fi";
import { Footer } from "../components/Footer";
import { SearchModal } from "../components/SearchModal";
import { ChatSidebar } from "../components/ChatSidebar";
import { TokenEditor } from "../components/dev/TokenEditor";
import { useSearchModal } from "../hooks/useSearchModal";
import { useChatSidebar } from "../hooks/useChatSidebar";

export const Route = createRootRoute({
	component: RootLayout,
});

function RootLayout(): ReactElement {
	const { data: session, isPending } = useSession();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isLandingPage = pathname === "/" && !session?.user;
	const isAuthPage = pathname === "/login" || pathname === "/signup";
	const [showTokenEditor, setShowTokenEditor] = useState(false);
	const {
		isOpen: searchOpen,
		open: openSearch,
		close: closeSearch,
	} = useSearchModal();
	const {
		isOpen: chatOpen,
		toggle: toggleChat,
		close: closeChat,
	} = useChatSidebar();

	if (
		pathname.startsWith("/dev") ||
		pathname.startsWith("/s/") ||
		pathname === "/nerve" ||
		isAuthPage ||
		isLandingPage
	) {
		return <Outlet />;
	}

	return (
		<div className="bg-surface text-fg flex min-h-screen flex-col font-sans antialiased">
			<nav
				className={`border-line bg-nav-backdrop fixed top-0 z-50 w-full border-b backdrop-blur-xl ${
					isLandingPage ? "landing-animate-header" : ""
				}`}
			>
				<div className="mx-auto flex h-20 max-w-[960px] items-center justify-between px-6">
					<div className="flex items-center gap-4">
						<Link
							to="/"
							search={{
								mode: undefined,
								tag: undefined,
								type: undefined,
								summary: undefined,
							}}
							className="text-2xl font-semibold tracking-tight"
						>
							clearvoid
						</Link>
						{import.meta.env.DEV && (
							<button
								type="button"
								onClick={() => setShowTokenEditor((v) => !v)}
								className="rounded bg-yellow-500 px-2 py-0.5 text-[11px] font-bold tracking-wider text-black"
							>
								DEV
							</button>
						)}
					</div>
					<div className="flex items-center gap-8">
						{!isPending && session?.user && (
							<div className="flex items-center gap-6">
								<button
									type="button"
									onClick={openSearch}
									className="border-line text-fg-ghost hover:text-fg-secondary hover:border-line-strong flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors"
								>
									<FiSearch className="text-sm" />
									<span className="hidden sm:inline">
										Search
									</span>
									<kbd className="border-line-strong hidden rounded border px-1 py-0.5 text-[10px] font-light sm:inline">
										{navigator.platform?.includes("Mac")
											? "\u2318K"
											: "Ctrl+K"}
									</kbd>
								</button>
								<button
									type="button"
									onClick={toggleChat}
									data-testid="chat-toggle-btn"
									className={`border-line hover:text-fg-secondary hover:border-line-strong flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${chatOpen ? "text-accent border-accent-muted" : "text-fg-ghost"}`}
								>
									<FiMessageSquare className="text-sm" />
									<span className="hidden sm:inline">
										Chat
									</span>
								</button>
								<span className="text-fg-faint text-sm">
									{session.user.email}
								</span>
								<button
									type="button"
									onClick={() => signOut()}
									className="text-fg-muted hover:text-accent text-[11px] font-light tracking-[0.2em] uppercase transition-colors"
								>
									Sign out
								</button>
							</div>
						)}
						{!isPending && !session?.user && (
							<>
								<Link
									to="/pricing"
									className="text-fg-muted hover:text-accent text-[11px] font-light tracking-[0.2em] uppercase transition-colors"
								>
									Pricing
								</Link>
								<Link
									to="/login"
									search={{ redirect: undefined }}
									className="border-accent-muted hover:bg-accent hover:text-surface border px-6 py-2.5 text-[11px] font-light tracking-[0.2em] uppercase transition-all duration-300"
								>
									Login
								</Link>
							</>
						)}
					</div>
				</div>
			</nav>
			<main className="flex-1 pt-20">
				<Outlet />
			</main>
			<Footer />
			<SearchModal
				key={String(searchOpen)}
				open={searchOpen}
				onClose={closeSearch}
			/>
			{session?.user && (
				<ChatSidebar isOpen={chatOpen} onClose={closeChat} />
			)}
			{import.meta.env.DEV && showTokenEditor && (
				<TokenEditor onClose={() => setShowTokenEditor(false)} />
			)}
		</div>
	);
}
